import React, { useState } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import {
  FaUserGraduate,
  FaAward,
  FaUsers,
  FaCertificate,
  FaEnvelope,
  FaHeart,
  FaUser,
  FaIdBadge,
  FaCalendarAlt,
  FaGenderless,
  FaPhoneAlt,
  FaGraduationCap,
  FaMapMarkerAlt,
  FaListAlt,
  FaClock,
  FaBullseye,
  FaBuilding,
  FaCloudUploadAlt,
  FaPaperPlane,
  FaShieldAlt,
  FaSyncAlt,
} from "react-icons/fa";
import "./Interns.css";

const FEATURES = [
  {
    icon: FaUserGraduate,
    title: "Learn & Grow",
    text: "Gain real-world experience in community development.",
  },
  {
    icon: FaAward,
    title: "Make Impact",
    text: "Work on meaningful projects that create change.",
  },
  {
    icon: FaUsers,
    title: "Mentorship",
    text: "Learn from experts and grow your skills.",
  },
  {
    icon: FaCertificate,
    title: "Certificate",
    text: "Get a certificate of completion for your valuable contribution.",
  },
];

const Interns = () => {
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeInOut" } },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage("");

    const form = e.target;
    const formData = new FormData(form);

    try {
      const res = await fetch("/backend/submit_intern.php", {
        method: "POST",
        body: formData,
      });

      const data = await res.json().catch(() => ({ success: false, message: "Invalid server response." }));

      if (res.ok && data.success) {
        setMessage("Application submitted successfully.");
        form.reset();
        setCvFile(null);
      } else {
        setMessage("Error: " + (data.message || "Submission failed."));
      }
    } catch (err) {
      console.error(err);
      setMessage("Network or server error. Please check the backend endpoint.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      {/* ---------- Hero ---------- */}
      <section className="interns-hero">
        <div
          className="interns-hero__bg"
          style={{ backgroundImage: "url(/assets/images/backgrounds/Intern_1521x482.jpg)" }}
        />
        <div className="interns-hero__overlay" />
        <div className="container">
          <div className="interns-hero__inner">
            <motion.h2
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              Interns
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="interns-hero__subtitle"
            >
              Vikash offers internship opportunities for those passionate
              about community development.
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="interns-hero__divider"
            >
              <span className="interns-hero__line" />
              <FaHeart />
              <span className="interns-hero__line" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ---------- Feature strip (overlaps hero) ---------- */}
      <Container>
        <motion.div
          className="interns-features"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
        >
          <Row className="g-0">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <Col key={f.title} xs={6} md={3} className="interns-feature-col">
                  <div className="interns-feature">
                    <div className="interns-feature__icon">
                      <Icon />
                    </div>
                    <div>
                      <div className="interns-feature__title">{f.title}</div>
                      <div className="interns-feature__text">{f.text}</div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </motion.div>
      </Container>

      <Container className="interns-body">
        {/* ---------- Intro ---------- */}
        <motion.div
          className="interns-intro"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <h3 className="interns-heading">
            Interns
            <span className="interns-heading__underline" />
          </h3>
          <p className="interns-intro__text">
            Vikash internship programme is a management improvement model for
            young enthusiasts who are self-driven to work in the social sector.
          </p>
          <p className="interns-intro__mail">
            <FaEnvelope className="interns-intro__mail-icon" />
            Please mail us at <a href="mailto:hr@vikash.org">hr@vikash.org</a>{" "}
            mentioning "Application for Internship".
          </p>
        </motion.div>

        {/* ---------- Form ---------- */}
        <motion.div
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <Card className="interns-card">
            <h3 className="interns-card__title">
              <FaSyncAlt className="interns-card__title-icon interns-card__title-icon--left" />
              Apply for Internship
              <FaSyncAlt className="interns-card__title-icon interns-card__title-icon--right" />
            </h3>
            <span className="interns-card__title-underline" />

            <Form onSubmit={handleSubmit} className="interns-form">
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Full Name<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaUser className="input-icon" />
                      <Form.Control
                        name="name"
                        type="text"
                        placeholder="Enter your name"
                        required
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Student ID<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaIdBadge className="input-icon" />
                      <Form.Control
                        name="student_id"
                        type="text"
                        placeholder="Enter student ID"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Date of Birth<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaCalendarAlt className="input-icon" />
                      <Form.Control name="dob" type="date" />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Gender</Form.Label>
                    <div className="input-icon-wrap">
                      <FaGenderless className="input-icon" />
                      <Form.Select name="gender">
                        <option value="">--Select--</option>
                        <option>Male</option>
                        <option>Female</option>
                        <option>Other</option>
                      </Form.Select>
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Mobile<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaPhoneAlt className="input-icon" />
                      <Form.Control
                        name="phone"
                        type="tel"
                        placeholder="Enter your mobile"
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Email<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaEnvelope className="input-icon" />
                      <Form.Control
                        name="mail"
                        type="email"
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Qualification<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaGraduationCap className="input-icon" />
                      <Form.Control
                        name="qualification"
                        type="text"
                        placeholder="Enter qualification"
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Address of Academic Institution<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaMapMarkerAlt className="input-icon" />
                      <Form.Control
                        name="adress_institution"
                        as="textarea"
                        rows={1}
                        placeholder="Enter address"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Postal Address<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaMapMarkerAlt className="input-icon" />
                      <Form.Control
                        name="postal_address"
                        as="textarea"
                        rows={1}
                        placeholder="Enter postal address"
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Type of Internship<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaListAlt className="input-icon" />
                      <Form.Select name="type_of_internship">
                        <option value="">--Select--</option>
                        <option>Short-term</option>
                        <option>Long-term</option>
                      </Form.Select>
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Duration of Internship<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaClock className="input-icon" />
                      <Form.Select name="duration_of_internship">
                        <option value="">--Select--</option>
                        <option>1 Month</option>
                        <option>3 Months</option>
                        <option>6 Months</option>
                      </Form.Select>
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Preferred Location<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaMapMarkerAlt className="input-icon" />
                      <Form.Select name="preferred_location">
                        <option value="">--Select--</option>
                        <option>Bhubaneswar</option>
                        <option>Cuttack</option>
                        <option>Remote</option>
                      </Form.Select>
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Area of Interest<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaBullseye className="input-icon" />
                      <Form.Select name="internship">
                        <option value="">--Select--</option>
                        <option>Community Development</option>
                        <option>Education</option>
                        <option>Health</option>
                      </Form.Select>
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Institute Point Person<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaUser className="input-icon" />
                      <Form.Control
                        name="institute_point_person"
                        type="text"
                        placeholder="Enter name"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Institute Contact<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaPhoneAlt className="input-icon" />
                      <Form.Control
                        name="institute_contact"
                        type="tel"
                        placeholder="Enter mobile"
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Institute Email<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaEnvelope className="input-icon" />
                      <Form.Control
                        name="institute_email"
                        type="email"
                        placeholder="Enter email"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      No. of Hours/Day<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaClock className="input-icon" />
                      <Form.Select name="hours">
                        <option value="">--Select--</option>
                        <option>2 Hours</option>
                        <option>4 Hours</option>
                        <option>6 Hours</option>
                      </Form.Select>
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Expected Start Date<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaCalendarAlt className="input-icon" />
                      <Form.Control name="start_date" type="date" />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Expected End Date<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaCalendarAlt className="input-icon" />
                      <Form.Control name="end_date" type="date" />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Organisation / Institution<span className="required">*</span>
                    </Form.Label>
                    <div className="input-icon-wrap">
                      <FaBuilding className="input-icon" />
                      <Form.Control
                        name="institution"
                        type="text"
                        placeholder="Enter organisation / institution"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-4">
                <Form.Label>Attach Your CV</Form.Label>
                <div className="interns-file">
                  <FaCloudUploadAlt className="interns-file__icon" />
                  <Form.Control
                    name="document"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setCvFile(e.target.files[0])}
                    className="interns-file__input"
                  />
                  <span className="interns-file__hint">PDF, DOC, DOCX (Max. 5MB)</span>
                </div>
              </Form.Group>

              <div className="text-center">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={submitting}
                  className="interns-submit"
                >
                  <FaPaperPlane className="interns-submit__icon" />
                  {submitting ? "Submitting..." : "Submit Application"}
                </Button>
              </div>
            </Form>

            {message && <div className="mt-3 alert alert-info">{message}</div>}
          </Card>
        </motion.div>

        <div className="interns-trust">
          <FaShieldAlt />
          Your information is safe with us. We respect your privacy.
        </div>
      </Container>
      <Footer />
    </>
  );
};

export default Interns;